import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useI18n } from '../i18n/index.tsx';
import { api } from '../api/client.ts';
import { useApiError } from '../api/describe.ts';
import type {
  AiStatus,
  CertificatesView,
  NotificationEventName,
  PanelInstallation,
  PanelTls,
  Session,
  TelegramView,
} from '../api/types.ts';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Confirm,
  Checkbox,
  CopyableValue,
  EmptyState,
  Field,
  Notice,
  Skeleton,
  TextInput,
} from '../components/ui.tsx';
import { Technical, usePermission } from '../components/operational.tsx';
import { useExpertMode } from '../expert/index.tsx';
import type { MessageKey } from '../i18n/messages.ts';
import '../components/operational.css';

/**
 * Settings: the certificate, the notifications, and the switch that turns the
 * technical detail on.
 *
 * Certificates live here rather than in the main navigation on purpose. A
 * customer does not wake up wanting to manage a certificate; they want their
 * panel to open without a warning, and that is a setting of the panel. What
 * the section must never do is soften the language: a self-signed certificate
 * is called an internal certificate and is described as not trusted, in both
 * languages, because the alternative word for it would be a lie a customer
 * could act on.
 */
export function Settings({ session }: { session: Session }): ReactNode {
  const { t } = useI18n();

  return (
    <>
      <header className="page-header">
        <div>
          <h1>{t('settings.title')}</h1>
          <p className="muted">{session.organization.name}</p>
        </div>
      </header>

      <WhoYouAre session={session} />

      <Certificates />
      <Telegram />
      <Advanced />
    </>
  );
}

/**
 * Two questions, two answers.
 *
 * Every Telegram customer is the owner of their own organization, so showing
 * "owner" alone read as though a customer owned PanelTunnel itself. The role
 * says what this person owns; the level says whether they run the
 * installation. An operator who is both sees both, because replacing one with
 * the other would hide the fact that they are also somebody's tenant.
 */
function WhoYouAre({ session }: { session: Session }): ReactNode {
  const { t } = useI18n();
  return (
    <Card>
      <CardHeader title={session.user.displayName} />
      <ul className="key-values">
        <li>
          <span>{t('account.roleLabel')}</span>
          <strong>{t(`account.role.${session.user.role}` as never)}</strong>
        </li>
        <li>
          <span>{t('account.levelLabel')}</span>
          <strong>
            {session.platformAdmin ? t('account.level.platform') : t('account.level.tenant')}
          </strong>
        </li>
      </ul>
    </Card>
  );
}

// ------------------------------------------------------------ certificates

function Certificates(): ReactNode {
  const { t } = useI18n();
  const [view, setView] = useState<CertificatesView | undefined>();
  const [panels, setPanels] = useState<readonly PanelInstallation[]>([]);
  const [error, setError] = useState<string | undefined>();

  const load = useCallback(async () => {
    try {
      const [certificates, installed] = await Promise.all([
        api.certificates(),
        api.installedPanels(),
      ]);
      setView(certificates);
      setPanels(installed);
      setError(undefined);
    } catch {
      setError(t('error.network'));
    }
  }, [t]);

  useEffect(() => {
    // Wrapped rather than called directly: `load` sets state, and an effect
    // that does so in its own body is a cascading render. The work is
    // asynchronous anyway — every setState in it happens after an await.
    void (async () => {
      await load();
    })();
  }, [load]);

  return (
    <Card>
      <CardHeader title={t('ssl.title')} description={t('ssl.subtitle')} />
      {error && view ? <Notice tone="danger">{error}</Notice> : null}
      {!view ? (
        error ? (
          // Not a skeleton. A spinner that never resolves says "still
          // loading" forever, which is the one thing that is definitely not
          // happening, and it leaves the customer nothing to press.
          <EmptyState
            title={t('error.network')}
            action={
              <Button variant="secondary" onClick={() => void load()}>
                {t('common.retry')}
              </Button>
            }
          />
        ) : (
          <Skeleton lines={4} />
        )
      ) : (
        <div className="settings-section">
          {view.panels.length === 0 ? (
            <p className="muted">{t('backups.noPanels')}</p>
          ) : (
            view.panels.map((panel) => (
              <PanelCertificate
                key={panel.installationId}
                tls={panel}
                name={
                  panels.find((row) => row.id === panel.installationId)?.serverName ??
                  panel.installationId
                }
                hasDns={Boolean(view.dns)}
                onChanged={() => void load()}
              />
            ))
          )}

          <DnsCredential
            configured={view.dns !== null}
            zone={view.dns?.zone ?? null}
            onSaved={() => void load()}
          />
        </div>
      )}
    </Card>
  );
}

function PanelCertificate({
  tls,
  name,
  hasDns,
  onChanged,
}: {
  tls: PanelTls;
  name: string;
  hasDns: boolean;
  onChanged: () => void;
}): ReactNode {
  const { t, formatDateTime } = useI18n();
  const describe = useApiError();
  const request = usePermission('deployment.create');
  const [open, setOpen] = useState(false);
  const [domain, setDomain] = useState(tls.domain ?? '');
  const [email, setEmail] = useState('');
  const [production, setProduction] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<ReactNode>();

  const renewalKey: MessageKey | undefined =
    tls.renewal === 'expired'
      ? 'ssl.renewalExpired'
      : tls.renewal === 'due'
        ? 'ssl.renewalDue'
        : tls.renewal === 'scheduled'
          ? 'ssl.renewalScheduled'
          : undefined;

  return (
    <div className="op-row">
      <div className="op-row__head">
        <div className="op-row__title">
          <span>{name}</span>
          {tls.kind === 'trusted' ? <Badge tone="success">{t('ssl.trusted')}</Badge> : null}
          {/* Red, not amber. A panel serving an expired certificate is one
              every browser is already refusing, which is worse for the
              customer than one that never had a real certificate at all. */}
          {tls.kind === 'expired' ? <Badge tone="danger">{t('ssl.expired')}</Badge> : null}
          {tls.kind === 'internal_bootstrap' ? (
            <Badge tone="warning">{t('ssl.bootstrap')}</Badge>
          ) : null}
        </div>
        {tls.expiresAt ? (
          <span className="muted">{t('ssl.expires', { when: formatDateTime(tls.expiresAt) })}</span>
        ) : null}
      </div>

      {tls.kind === 'trusted' ? (
        <>
          <p className="muted">
            {t('ssl.trustedHelp', { domains: (tls.domains ?? []).join(', ') })}
          </p>
          <p>
            {tls.daysRemaining !== undefined
              ? t('ssl.daysRemaining', { days: tls.daysRemaining })
              : null}{' '}
            {renewalKey ? t(renewalKey) : null}
          </p>
        </>
      ) : null}

      {tls.kind === 'expired' ? (
        <Notice tone="danger" title={t('ssl.expired')}>
          {t('ssl.expiredHelp', { domains: (tls.domains ?? []).join(', ') })}
        </Notice>
      ) : null}

      {tls.kind === 'internal_bootstrap' ? (
        <>
          {/* The whole point of this screen. Stated plainly, before any
              action, and never shortened to a colour or an icon. */}
          <Notice tone="warning" title={t('ssl.bootstrap')}>
            {t('ssl.bootstrapHelp')}
          </Notice>
          <p className="muted">{t('ssl.bootstrapAction')}</p>
        </>
      ) : null}

      {open ? (
        <>
          {!hasDns ? <Notice tone="warning">{t('ssl.needsDns')}</Notice> : null}
          <div className="form-row">
            <Field label={t('ssl.domain')} help={t('ssl.domainHelp')} required>
              {(props) => (
                <TextInput
                  {...props}
                  value={domain}
                  inputMode="url"
                  placeholder="panel.example.com"
                  onChange={(event) => setDomain(event.target.value)}
                />
              )}
            </Field>
            <Field label={t('ssl.email')} help={t('ssl.emailHelp')} required>
              {(props) => (
                <TextInput
                  {...props}
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              )}
            </Field>
          </div>
          <Checkbox
            label={t('ssl.production')}
            help={t('ssl.productionHelp')}
            checked={production}
            onChange={setProduction}
          />
          <Notice tone="info">{t('ssl.replaceNote')}</Notice>
          <div className="op-row__actions">
            <Button
              busy={busy}
              disabled={!request.allowed || !hasDns || domain.trim() === '' || email.trim() === ''}
              title={request.reason}
              onClick={() => {
                setBusy(true);
                setMessage(undefined);
                void api
                  .requestCertificate({
                    installationId: tls.installationId,
                    domains: [domain.trim()],
                    email: email.trim(),
                    production,
                  })
                  .then(() => {
                    setMessage(<Notice tone="success">{t('ssl.requested')}</Notice>);
                    onChanged();
                  })
                  .catch((raised: unknown) =>
                    setMessage(<Notice tone="danger">{describe(raised)}</Notice>),
                  )
                  .finally(() => setBusy(false));
              }}
            >
              {t('ssl.request')}
            </Button>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </Button>
          </div>
        </>
      ) : (
        <div className="op-row__actions">
          <Button
            variant="secondary"
            disabled={!request.allowed}
            title={request.reason}
            onClick={() => setOpen(true)}
          >
            {t('ssl.getOne')}
          </Button>
        </div>
      )}

      {message}

      <Technical>
        <div>installation {tls.installationId}</div>
        <div>kind {tls.kind}</div>
        {tls.certificateId ? <div>certificate {tls.certificateId}</div> : null}
        {tls.reason ? <div>{tls.reason}</div> : null}
      </Technical>
    </div>
  );
}

function DnsCredential({
  configured,
  zone,
  onSaved,
}: {
  configured: boolean;
  zone: string | null;
  onSaved: () => void;
}): ReactNode {
  const { t } = useI18n();
  const describe = useApiError();
  const manage = usePermission('organization.manage');
  const [token, setToken] = useState('');
  const [zoneInput, setZoneInput] = useState(zone ?? '');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<ReactNode>();

  return (
    <div className="op-row">
      <div className="op-row__title">
        <span>{t('ssl.dnsToken')}</span>
        {configured ? <Badge tone="success">{t('common.on')}</Badge> : null}
      </div>
      {configured ? <p className="muted">{t('ssl.dnsConfigured')}</p> : null}
      <div className="form-row">
        <Field label={t('ssl.dnsToken')} help={t('ssl.dnsTokenHelp')} required>
          {(props) => (
            <TextInput
              {...props}
              type="password"
              value={token}
              autoComplete="off"
              onChange={(event) => setToken(event.target.value)}
            />
          )}
        </Field>
        <Field label={t('ssl.dnsZone')}>
          {(props) => (
            <TextInput
              {...props}
              value={zoneInput}
              placeholder="example.com"
              onChange={(event) => setZoneInput(event.target.value)}
            />
          )}
        </Field>
      </div>
      <div className="op-row__actions">
        <Button
          busy={busy}
          disabled={!manage.allowed || token.trim().length < 20}
          title={manage.reason}
          onClick={() => {
            setBusy(true);
            setMessage(undefined);
            void api
              .saveDnsCredential(token.trim(), zoneInput.trim() || undefined)
              .then(() => {
                // Cleared the moment it is stored. The field held a credential
                // that can edit every record in the customer's zone; leaving it
                // on screen is leaving it in the page for whoever walks past.
                setToken('');
                setMessage(<Notice tone="success">{t('ssl.dnsSaved')}</Notice>);
                onSaved();
              })
              .catch((raised: unknown) =>
                setMessage(<Notice tone="danger">{describe(raised)}</Notice>),
              )
              .finally(() => setBusy(false));
          }}
        >
          {t('ssl.dnsSave')}
        </Button>
      </div>
      {message}
    </div>
  );
}

// --------------------------------------------------------------- telegram

const EVENT_LABEL: Record<NotificationEventName, MessageKey> = {
  'deployment.completed': 'event.deployment.completed',
  'deployment.failed': 'event.deployment.failed',
  'server.offline': 'event.server.offline',
  'node.offline': 'event.node.offline',
  'tunnel.down': 'event.tunnel.down',
  'tunnel.degraded': 'event.tunnel.degraded',
  'certificate.expiring': 'event.certificate.expiring',
  'backup.failed': 'event.backup.failed',
  'disk.high_usage': 'event.disk.high_usage',
};

function Telegram(): ReactNode {
  const { t, locale, formatDateTime } = useI18n();
  const describe = useApiError();
  const manage = usePermission('organization.manage');
  const [view, setView] = useState<TelegramView | undefined>();
  const [code, setCode] = useState<{ code: string; expiresAt: string } | undefined>();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<ReactNode>();
  const [error, setError] = useState<string | undefined>();
  const [unlinking, setUnlinking] = useState<string | undefined>();

  const load = useCallback(async () => {
    try {
      setView(await api.telegram());
      setError(undefined);
    } catch {
      // Emphatically not `{ configured: false, chats: [] }`. That is the
      // shape of an account that has never connected a chat, and rendering a
      // failed request as one tells a customer their connected chats are
      // gone. A request that did not arrive knows nothing about the account.
      setError(t('error.network'));
    }
  }, [t]);

  useEffect(() => {
    // Wrapped rather than called directly: `load` sets state, and an effect
    // that does so in its own body is a cascading render. The work is
    // asynchronous anyway — every setState in it happens after an await.
    void (async () => {
      await load();
    })();
  }, [load]);

  if (!view) {
    return (
      <Card>
        <CardHeader title={t('telegram.title')} description={t('telegram.subtitle')} />
        {error ? (
          <EmptyState
            title={t('error.network')}
            action={
              <Button variant="secondary" onClick={() => void load()}>
                {t('common.retry')}
              </Button>
            }
          />
        ) : (
          <Skeleton lines={3} />
        )}
      </Card>
    );
  }

  const toggle = (event: NotificationEventName, enabled: boolean): void => {
    const muted = enabled ? view.muted.filter((name) => name !== event) : [...view.muted, event];
    setBusy(true);
    void api
      .saveNotificationPreferences(muted)
      .then((result) => {
        setView({ ...view, muted: result.muted });
        setMessage(<Notice tone="success">{t('telegram.saved')}</Notice>);
      })
      .catch((raised: unknown) => setMessage(<Notice tone="danger">{describe(raised)}</Notice>))
      .finally(() => setBusy(false));
  };

  return (
    <Card>
      <CardHeader title={t('telegram.title')} description={t('telegram.subtitle')} />

      {error ? <Notice tone="danger">{error}</Notice> : null}

      {!view.configured ? (
        <Notice tone="neutral">{t('telegram.notConfigured')}</Notice>
      ) : (
        <div className="settings-section">
          {/* Said before anything is connected, because "a bot that manages my
              servers" is what a customer will assume otherwise. */}
          <Notice tone="info">{t('telegram.safety')}</Notice>

          {view.chats.length > 0 ? (
            <div className="op-list">
              <h3 className="op-list__heading">{t('telegram.linked')}</h3>
              {view.chats.map((chat) => (
                <div className="op-row" key={chat.chatId}>
                  <div className="op-row__head">
                    <div className="op-row__title">
                      <span>{t('telegram.chatLabel', { suffix: chat.chatIdSuffix })}</span>
                      {chat.unreachableAt ? <Badge tone="danger">{t('common.off')}</Badge> : null}
                    </div>
                    <span className="muted">
                      {t('telegram.linkedAt', { when: formatDateTime(chat.linkedAt) })}
                    </span>
                  </div>
                  {chat.unreachableAt ? (
                    <Notice tone="warning">{t('telegram.unreachable')}</Notice>
                  ) : null}
                  <div className="op-row__actions">
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={!manage.allowed}
                      title={manage.reason}
                      onClick={() => setUnlinking(chat.chatId)}
                    >
                      {t('telegram.unlink')}
                    </Button>
                  </div>
                  {unlinking === chat.chatId ? (
                    <Confirm
                      title={t('telegram.unlink')}
                      confirmLabel={t('telegram.unlink')}
                      onCancel={() => setUnlinking(undefined)}
                      onConfirm={() => {
                        setUnlinking(undefined);
                        void api
                          .unlinkTelegramChat(chat.chatId)
                          .then(() => void load())
                          .catch((raised: unknown) =>
                            setMessage(<Notice tone="danger">{describe(raised)}</Notice>),
                          );
                      }}
                    >
                      {t('telegram.unlinkConfirm')}
                    </Confirm>
                  ) : null}
                  <Technical>
                    <div>chat {chat.chatId}</div>
                    {chat.unreachableReason ? <div>{chat.unreachableReason}</div> : null}
                  </Technical>
                </div>
              ))}
            </div>
          ) : null}

          {code ? (
            <div className="op-row">
              <p className="muted">
                {t('telegram.linkHelp', { when: formatDateTime(code.expiresAt) })}
              </p>
              <CopyableValue value={code.code} label={t('telegram.code')} />
            </div>
          ) : (
            <div className="op-row__actions">
              <Button
                variant="secondary"
                disabled={!manage.allowed}
                title={manage.reason}
                onClick={() => {
                  void api
                    .telegramLinkCode(locale)
                    .then(setCode)
                    .catch((raised: unknown) =>
                      setMessage(<Notice tone="danger">{describe(raised)}</Notice>),
                    );
                }}
              >
                {t('telegram.link')}
              </Button>
            </div>
          )}

          <h3>{t('telegram.notifications')}</h3>
          <p className="muted">{t('telegram.notificationsHelp')}</p>
          {!manage.allowed ? <Notice tone="neutral">{t('permission.readOnly')}</Notice> : null}
          <div className="event-toggles">
            {view.events.map((event) => (
              <Checkbox
                key={event}
                label={t(EVENT_LABEL[event])}
                checked={!view.muted.includes(event)}
                disabled={busy || !manage.allowed}
                onChange={(enabled) => toggle(event, enabled)}
              />
            ))}
          </div>

          {message}
        </div>
      )}
    </Card>
  );
}

// --------------------------------------------------------------- advanced

function Advanced(): ReactNode {
  const { t } = useI18n();
  const { expert, setExpert } = useExpertMode();

  return (
    <Card>
      <CardHeader title={t('settings.advanced')} />
      <Checkbox
        label={t('settings.expert')}
        help={t('settings.expertHelp')}
        checked={expert}
        onChange={setExpert}
      />
      <AiAssistance />
    </Card>
  );
}

/**
 * Whether an AI service is configured, for the accounts that may know.
 *
 * The point of the section is the sentence it shows when nothing is
 * configured: not "AI unavailable", which reads as a fault, but that nothing
 * needs one. This platform installs, repairs, backs up and monitors without
 * it, and a settings screen implying otherwise would be the first step toward
 * somebody treating an optional service as a dependency.
 *
 * Only requested for a session permitted to read diagnostics — a customer has
 * no use for an internal endpoint — and a refusal is simply nothing rendered,
 * because a locked section is an invitation to ask for a key.
 */
function AiAssistance(): ReactNode {
  const { t } = useI18n();
  const diagnostics = usePermission('diagnostics.read');
  const [status, setStatus] = useState<AiStatus | undefined>();

  useEffect(() => {
    if (!diagnostics.allowed) return;
    void (async () => {
      setStatus(await api.aiStatus().catch(() => undefined));
    })();
  }, [diagnostics.allowed]);

  if (!diagnostics.allowed || !status) return null;

  return (
    <div className="settings-section">
      <h3>{t('ai.title')}</h3>
      {!status.configured ? (
        <Notice tone="neutral">{t('ai.disabled')}</Notice>
      ) : (
        <>
          <p>{t('ai.configured', { endpoint: status.endpoint ?? '' })}</p>
          <Notice tone={status.available ? 'success' : 'warning'}>
            {status.available ? t('ai.reachable') : t('ai.unreachable')}
          </Notice>
        </>
      )}
      <p className="muted">{t('ai.neverRequired')}</p>
      <Technical>
        <div>provider {status.provider}</div>
        <div>required {String(status.required)}</div>
        {status.tasks.map((task) => (
          <div key={task.task}>
            {task.task} → {task.model}
            {task.endpoint ? ` @ ${task.endpoint}` : ''}
          </div>
        ))}
        {status.detail ? <div>{status.detail}</div> : null}
      </Technical>
    </div>
  );
}
